import { Box, Chip, Typography } from "@mui/material";
import React from "react";

import { isRossPriceKeptDecision } from "../../constants";

import RossReviewable from "./RossReviewable";

export const formatDateOrdinal = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "short" });

  const getOrdinal = (n) => {
    if (n > 3 && n < 21) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${getOrdinal(day)} of ${month}`;
};

// Format the original action to show a cleaner description
// For PPC actions, show "Increase PPC" or "Decrease PPC"
// For Price actions, show "Reduce Price Drastically" or "Increase Price"
export const getRossActionDescription = (originalAction) => {
  const action = originalAction.toLowerCase();
  if (action.includes("increase ppc")) {
    return "Increase PPC";
  }
  if (action.includes("decrease ppc") || action.includes("reduce ppc")) {
    return "Decrease PPC";
  }
  if (action.includes("reduce price")) {
    return "Reduce Price Drastically";
  }
  if (action.includes("increase price")) {
    return "Increase Price";
  }
  // Fallback: return the second part (method) if available, otherwise the first part
  const parts = originalAction.split(" - ");
  return parts[1] || parts[0] || originalAction;
};

// ": $A → $B" for the price or PPC change Ross is analyzing, "" without data
const getRossChangeDisplay = ({
  originalAction,
  originalPrice,
  targetPrice,
  originalPpc,
  targetPpc,
}) => {
  const action = originalAction.toLowerCase();
  if (action.includes("ppc") && originalPpc != null && targetPpc != null) {
    return `: $${originalPpc.toFixed(2)} → $${targetPpc.toFixed(2)}`;
  }
  if (action.includes("price") && targetPrice != null) {
    return `: $${(originalPrice || 0).toFixed(2)} → $${targetPrice.toFixed(2)}`;
  }
  return "";
};

// Helper to get impact color based on label
const getImpactColor = (impactLabel) => {
  if (!impactLabel) return { bg: "#e8f5e9", text: "#2e7d32" };
  const label = impactLabel.toLowerCase();
  if (label.includes("worked") || label.includes("kept"))
    return { bg: "#e8f5e9", text: "#2e7d32" };
  if (label.includes("harmful")) return { bg: "#ffebee", text: "#c62828" };
  if (label.includes("neutral")) return { bg: "#fff3e0", text: "#ef6c00" };
  return { bg: "#e3f2fd", text: "#1565c0" };
};

const RossDecisionCard = ({
  asin,
  sku,
  executionId,
  store,
  marketplace,
  // ROSS-specific data from the decision object
  rossData,
  onRemove,
}) => {
  // Extract data from rossData
  const features = rossData?.features || {};
  const decision = rossData?.decision || {};

  // Two Ross cards share this layout:
  // - velocity analysis ("Velocity Analysis Complete"): a week after the seller
  //   accepted a recommendation, 7-day velocity before/after
  // - "Price Kept" (card_group "ross_sticky"): after an automatic Iris price
  //   increase, sales held up over the evaluation window, so the price stays.
  //   It has its own feature names: units/day over `window_days`, the
  //   velocity_drop ratio, days of supply at evaluation time, and the prices in
  //   the decision (previous_price = before the increase, target_price = kept).
  const isPriceKept = isRossPriceKeptDecision(rossData);
  const windowDays = features.window_days || 10;

  const velocityPre =
    (isPriceKept ? features.pre_units_per_day : features.velocity_pre_7d) || 0;
  const velocityPost =
    (isPriceKept ? features.post_units_per_day : features.velocity_post_7d) ||
    0;
  const velocityDelta = isPriceKept
    ? velocityPost - velocityPre
    : features.velocity_delta || 0;
  const velocityDeltaPercentage = isPriceKept
    ? features.velocity_drop != null
      ? -features.velocity_drop * 100
      : 0
    : features.velocity_delta_percentage || 0;
  const daysOfSupplyPre = features.days_of_supply_pre || 0;
  const daysOfSupplyPost = features.days_of_supply_post || 0;
  // Price Kept shows profit per unit instead of days of supply: original
  // price vs kept price, minus unit costs (USD, already converted by the
  // backend). Either side is null when costs or the exchange rate are missing.
  const profitPerUnitPre = features.pre_profit_per_unit ?? null;
  const profitPerUnitPost = features.post_profit_per_unit ?? null;
  const originalAgent = features.original_agent || "iris";
  const originalAction = isPriceKept
    ? "Reduce Velocity - Increase Price"
    : features.original_action || "";
  const originalPrice =
    (isPriceKept ? decision.previous_price : features.original_price) || 0;
  const targetPrice = isPriceKept
    ? decision.target_price
    : features.target_price;
  const originalPpc = features.original_ppc;
  const targetPpc = features.target_ppc;
  const originalDecisionDate =
    (isPriceKept ? features.increase_date : features.original_decision_date) ||
    "";

  const impactLabel = isPriceKept ? "Price Kept" : decision.impact_label || "";

  const impactColors = getImpactColor(impactLabel);
  const isPositive = velocityDelta > 0;
  const daysOfSupplyDelta = daysOfSupplyPost - daysOfSupplyPre;
  const daysOfSupplyDeltaPercentage =
    daysOfSupplyPre > 0 ? (daysOfSupplyDelta / daysOfSupplyPre) * 100 : 0;
  const isDaysSupplyDecrease = daysOfSupplyDelta < 0;

  // Determine if the action intent is to increase or reduce velocity
  const isIncreaseVelocityIntent = () => {
    const action = originalAction.toLowerCase();
    // "Increase Velocity" actions or "Reduce Price" (which aims to increase velocity)
    if (action.includes("increase velocity")) return true;
    if (action.includes("reduce price")) return true;
    // "Reduce Velocity" actions or "Increase Price" (which aims to reduce velocity)
    if (action.includes("reduce velocity")) return false;
    if (action.includes("increase price")) return false;
    return true; // Default to increase velocity intent
  };

  const wantsIncreaseVelocity = isIncreaseVelocityIntent();

  // Velocity color: green if moved in desired direction
  // - If want increase velocity: green if delta > 0, red if delta < 0
  // - If want reduce velocity: green if delta < 0, red if delta > 0
  const getVelocityColor = () => {
    // Price Kept only exists when sales held up at the higher price
    if (isPriceKept) return "#4caf50";
    if (velocityDelta === 0) return "#ef6c00"; // Orange if no change
    if (wantsIncreaseVelocity) {
      return velocityDelta > 0 ? "#4caf50" : "#f44336";
    } else {
      return velocityDelta < 0 ? "#4caf50" : "#f44336";
    }
  };

  // Days of Supply color: green if moved in desired direction
  // - If want increase velocity: DOS should decrease (selling faster) → green if delta < 0
  // - If want reduce velocity: DOS should increase (selling slower) → green if delta > 0
  const getDaysOfSupplyColor = () => {
    if (daysOfSupplyDelta === 0) return "#ef6c00"; // Orange if no change
    if (wantsIncreaseVelocity) {
      return daysOfSupplyDelta < 0 ? "#4caf50" : "#f44336";
    } else {
      return daysOfSupplyDelta > 0 ? "#4caf50" : "#f44336";
    }
  };

  const velocityColor = getVelocityColor();
  const daysOfSupplyColor = getDaysOfSupplyColor();

  const formatProfit = (value) =>
    value == null
      ? "—"
      : `${value < 0 ? "-" : ""}$${Math.abs(value).toFixed(2)}`;
  const profitPerUnitDelta =
    profitPerUnitPre != null && profitPerUnitPost != null
      ? profitPerUnitPost - profitPerUnitPre
      : null;
  // A % change is only meaningful against a positive starting margin
  const profitPerUnitDeltaPercentage =
    profitPerUnitDelta != null && profitPerUnitPre > 0
      ? (profitPerUnitDelta / profitPerUnitPre) * 100
      : null;

  // Second before/after block: profit per unit on Price Kept, days of supply
  // on the velocity analysis card. Hidden on Price Kept with no profit data.
  const secondMetric = isPriceKept
    ? profitPerUnitPre == null && profitPerUnitPost == null
      ? null
      : {
          title: "Profit / Unit",
          before: formatProfit(profitPerUnitPre),
          after: formatProfit(profitPerUnitPost),
          change:
            profitPerUnitDeltaPercentage == null
              ? null
              : `(${profitPerUnitDeltaPercentage >= 0 ? "+" : "-"}${Math.abs(
                  profitPerUnitDeltaPercentage
                ).toFixed(1)}%)`,
          changeColor:
            profitPerUnitDelta > 0
              ? "#4caf50"
              : profitPerUnitDelta < 0
              ? "#f44336"
              : "#ef6c00",
        }
    : {
        title: "Days of Supply",
        before: `${daysOfSupplyPre}d`,
        after: `${daysOfSupplyPost}d`,
        change: `(${isDaysSupplyDecrease ? "-" : "+"}${Math.abs(
          daysOfSupplyDeltaPercentage
        ).toFixed(1)}%)`,
        changeColor: daysOfSupplyColor,
      };

  // Get action intent derived directly from the action string
  const getActionIntent = () => {
    const action = originalAction.toLowerCase();
    if (action.includes("reduce velocity")) return "Reduce Velocity";
    if (action.includes("increase velocity")) return "Increase Velocity";
    if (action.includes("reduce price")) return "Increase Velocity";
    if (action.includes("increase price")) return "Reduce Velocity";
    if (action.includes("profit")) return "Increase Profit";
    return "";
  };

  const actionIntentSentence = getActionIntent()
    ? getActionIntent().toLowerCase()
    : "improve performance";

  const priceDisplay = getRossChangeDisplay({
    originalAction,
    originalPrice,
    targetPrice,
    originalPpc,
    targetPpc,
  });

  return (
    <RossReviewable
      asin={asin}
      sku={sku}
      executionId={executionId}
      store={store}
      marketplace={marketplace}
      rossData={rossData}
      onRemove={onRemove}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            lineHeight: 1.5,
            fontStyle: "italic",
            fontSize: "0.78rem",
          }}
        >
          {originalDecisionDate && (
            <>
              On the{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: 700,
                  fontStyle: "normal",
                  color: "#111827",
                }}
              >
                {formatDateOrdinal(originalDecisionDate)}
              </Box>{" "}
            </>
          )}
          {isPriceKept ? (
            <>Iris automatically decided to </>
          ) : (
            <>
              you accepted an{" "}
              {originalAgent.charAt(0).toUpperCase() + originalAgent.slice(1)}{" "}
              recommendation to{" "}
            </>
          )}
          <Box
            component="span"
            sx={{ fontWeight: 700, fontStyle: "normal", color: "#111827" }}
          >
            {getRossActionDescription(originalAction)}
            {priceDisplay}
          </Box>{" "}
          in order to{" "}
          <Box
            component="span"
            sx={{ fontWeight: 700, fontStyle: "normal", color: "#111827" }}
          >
            {actionIntentSentence}
          </Box>
          .
        </Typography>
        <Box
          sx={{
            mt: 1.5,
            ml: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 400, color: "#6b7280" }}
          >
            {/* window_days is how long Ross measured sales at the new
                    price, not the time since the increase: Ross can evaluate
                    later than that (late data, missed runs, backfills). */}
            {isPriceKept
              ? `Here are the results after ${windowDays} days at the new price:`
              : "Here are the results a week later:"}
          </Typography>
          <Chip
            label={impactLabel || "Worked"}
            size="small"
            sx={{
              backgroundColor: impactColors.bg,
              color: impactColors.text,
              fontWeight: 600,
              fontSize: "0.72rem",
              textTransform: "capitalize",
            }}
          />
        </Box>
        {isPriceKept && decision.reasoning && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.75, color: "#6b7280" }}
          >
            {decision.reasoning}
          </Typography>
        )}
      </Box>

      {/* 7-Day Avg. Velocity Section */}
      <Box sx={{ mb: 2, mt: 2, maxWidth: "85%", pr: 4, pl: 2, ml: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            mb: 0.75,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#111827",
              fontSize: "0.75rem",
              ml: -2,
            }}
          >
            {isPriceKept
              ? `Units/Day (${windowDays}-day avg.)`
              : "7-Day Avg. Velocity"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "56px 8px auto",
            gridTemplateRows: "auto auto",
            alignItems: "start",
            columnGap: 4,
            rowGap: 0.25,
            width: "fit-content",
            ml: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              gridColumn: 1,
              gridRow: 1,
              color: "#9e9e9e",
              fontSize: "0.7rem",
              lineHeight: 1,
              pl: 0.5,
            }}
          >
            Before
          </Typography>
          <Typography
            variant="caption"
            sx={{
              gridColumn: 3,
              gridRow: 1,
              color: "#9e9e9e",
              fontSize: "0.7rem",
              lineHeight: 1,
              textAlign: "left",
            }}
          >
            After
          </Typography>
          <Box
            sx={{
              gridColumn: 1,
              gridRow: 2,
              minWidth: 52,
              display: "flex",
              alignItems: "center",
              pl: 0.5,
              mt: 1,
            }}
          >
            <Typography sx={{ color: "#6b7280", fontWeight: 500 }}>
              {velocityPre.toFixed(2)}
            </Typography>
          </Box>
          <Box
            sx={{
              gridColumn: 2,
              gridRow: 2,
              width: 8,
              display: "flex",
              justifyContent: "flex-start",
              ml: -4,
              mt: 1.5,
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "#9ca3af", fontWeight: 700, lineHeight: 1 }}
            >
              →
            </Typography>
          </Box>
          <Box
            sx={{
              gridColumn: 3,
              gridRow: 2,
              textAlign: "left",
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 0.5,
              }}
            >
              <Typography
                component="span"
                sx={{
                  color: "#374151",
                  fontWeight: 700,
                  fontSize: "1.25rem",
                }}
              >
                {velocityPost.toFixed(2)}
              </Typography>
              <Typography
                component="span"
                sx={{
                  color: velocityColor,
                  fontWeight: 700,
                  fontSize: "1.25rem",
                }}
              >
                ({isPositive ? "+" : ""}
                {velocityDeltaPercentage.toFixed(1)}%)
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Days of Supply (velocity analysis) / Profit per Unit (Price Kept) */}
      {secondMetric && (
        <Box sx={{ mb: 1.5, mt: 3, maxWidth: "85%", pr: 4, pl: 2, ml: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              mb: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#111827",
                fontSize: "0.75rem",
                ml: -2,
              }}
            >
              {secondMetric.title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "56px 8px auto",
              gridTemplateRows: "auto auto",
              alignItems: "start",
              columnGap: 4,
              rowGap: 0.25,
              width: "fit-content",
              ml: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                gridColumn: 1,
                gridRow: 1,
                color: "#9e9e9e",
                fontSize: "0.7rem",
                lineHeight: 1,
                pl: 0.5,
              }}
            >
              Before
            </Typography>
            <Typography
              variant="caption"
              sx={{
                gridColumn: 3,
                gridRow: 1,
                color: "#9e9e9e",
                fontSize: "0.7rem",
                lineHeight: 1,
                textAlign: "left",
              }}
            >
              After
            </Typography>
            <Box
              sx={{
                gridColumn: 1,
                gridRow: 2,
                minWidth: 52,
                display: "flex",
                alignItems: "center",
                pl: 0.5,
                mt: 1,
              }}
            >
              <Typography sx={{ color: "#6b7280", fontWeight: 500 }}>
                {secondMetric.before}
              </Typography>
            </Box>
            <Box
              sx={{
                gridColumn: 2,
                gridRow: 2,
                width: 8,
                display: "flex",
                justifyContent: "flex-start",
                ml: -4,
                mt: 1.5,
              }}
            >
              <Typography
                variant="h5"
                sx={{ color: "#9ca3af", fontWeight: 700, lineHeight: 1 }}
              >
                →
              </Typography>
            </Box>
            <Box
              sx={{
                gridColumn: 3,
                gridRow: 2,
                textAlign: "left",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.5,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: "#374151",
                    fontWeight: 700,
                    fontSize: "1.25rem",
                  }}
                >
                  {secondMetric.after}
                </Typography>
                {secondMetric.change && (
                  <Typography
                    component="span"
                    sx={{
                      color: secondMetric.changeColor,
                      fontWeight: 700,
                      fontSize: "1.25rem",
                    }}
                  >
                    {secondMetric.change}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </RossReviewable>
  );
};

export default RossDecisionCard;
